import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types';
import { RateSegment } from '@insurancemenu/imenu-components-lib/dist/types/productCardsTypings';
import { getDomainProductLine } from 'api/operations/domainServiceV2';
import { sortByProductLineId } from '@insurancemenu/imenu-components-lib/dist/utils/ProductLinesHelper';
import { makeAutoObservable, runInAction } from 'mobx';
import { getRootStoreInstance, RootStore } from 'store/rootStore';

export interface DomainProductLinesDelegateParams {
  rateSegment: RateSegment;
}

export interface DomainProductLineItem {
  productLineId: ProductLineId;
  isAvailableWithCurrentConfiguration: boolean;
}

export class DomainProductLinesDelegate {
  private readonly _rootStore: RootStore = getRootStoreInstance();
  private readonly _rateSegment: RateSegment;
  private _domainProductLineItems: DomainProductLineItem[] = [];
  private _isLoading = false;

  public get domainProductLineItems(): DomainProductLineItem[] {
    return this._domainProductLineItems;
  }

  public get isLoading(): boolean {
    return this._isLoading;
  }

  private _setIsLoading(isLoading: boolean): void {
    this._isLoading = isLoading;
  }

  public clearProductLines(): void {
    this._domainProductLineItems = [];
  }

  public async getDomainProductLines(
    formData: Record<string, string>,
    includeUnavailable: boolean = true,
  ): Promise<void> {
    this._setIsLoading(true);

    const { effectiveDate, zip, state, county, sic4 } = formData;
    const productLines = await getDomainProductLine({
      effectiveDate,
      zipCode: zip,
      state,
      county,
      agencyId: this._rootStore.userStore.imAgencyId,
      sponsorId: this._rootStore.userStore.sponsorId,
      ...(sic4 ? { sic: sic4 } : {}),
      includeUnavailable,
    });

    runInAction(() => {
      this._domainProductLineItems = productLines
        .filter((productLine) => productLine.rateSegment === this._rateSegment)
        .map((productLine) => ({
          productLineId: productLine.productLineId,
          isAvailableWithCurrentConfiguration: productLine.available,
        }))
        .sort((a, b) => sortByProductLineId(a.productLineId, b.productLineId));
      this._setIsLoading(false);
    });
  }

  constructor(params: DomainProductLinesDelegateParams) {
    this._rateSegment = params.rateSegment;

    makeAutoObservable(this, {}, { autoBind: true });
  }
}
